import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { module } from 'app/modules/equipment-registration/equipment-registration.module';
import { EquipRegistrationServices } from 'app/modules/equipment-registration/services/equipment-registration.service';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EMPTY, map, merge, Subject, switchMap, takeUntil } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { saveAs } from 'file-saver';
import { TwoLevelDeleteDialogComponent } from '../equipment-historical-data/delete-dialog/delete-dialog.component';
import { UpdateHistoricalDataComponent } from './update-historical-data/update-historical-data.component';

@Component({
  selector: 'app-equipment-historical-data',
  templateUrl: './equipment-historical-data.component.html',
  standalone: true,
  imports: module,
  styleUrls: ['./equipment-historical-data.component.scss']
})
export class EquipmentHistoricalDataComponent {
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  @ViewChild(MatSort) private _sort: MatSort;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  searchForm: UntypedFormGroup;
  dt: any//Datatable<any>;
  startingIndex: number = 0
  isLoading: boolean = false

  public filterData = {
    pageNumber: 1,
    pageSize: 10,
    wellName: '',
    reportType: '',
    startDate: null,// new Date(),
    endDate: null
  }

  constructor(
    public dialogRef: MatDialogRef<EquipmentHistoricalDataComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _formBuilder: UntypedFormBuilder,
    private equipmentServices: EquipRegistrationServices,
    private _changeDetectorRef: ChangeDetectorRef,
    public dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.setSearch();
    this.setDataTable();
    this.setPage({ offset: 0 });
  }

  ngAfterViewInit() {
    if (this._sort && this._paginator) {
      // Set the initial sort
      this._sort.sort({
        id: 'field',
        start: 'asc',
        disableClear: true,
      });

      // Mark for check
      this._changeDetectorRef.markForCheck();

      // If the user changes the sort order...
      this._sort.sortChange
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          // Reset back to the first page
          this._paginator.pageIndex = 0;

          this.onSort(this._sort)
        });

      // Get platform types if sort or page changes
      merge(this._sort.sortChange, this._paginator.page).pipe(
        switchMap(() => {
          // this.isLoading = true;

          let pageInfo = {
            'count': this._paginator.length,
            'pageSize': this._paginator.pageSize,
            'limit': this._paginator.pageSize,
            'offset': this._paginator.pageIndex
          }
          this.setPage(pageInfo); // Emit page number

          return EMPTY
        }),
        map(() => {
          this.isLoading = false;
        }),
      ).subscribe();
    }
  }

  setSearch() {
    this.searchForm = this._formBuilder.group({
      Page: [0],
      PageSize: [10],
      OrderBy: [null],
      OrderDir: [null],
      WellName: [null],
      PlatformName: [null],
      StringName: [null],
      FieldName: [null],
    });

    this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
  }

  setSize(size: number) {
    this.dt.page.size = size;
    this.dt.page.offset = 0
    this.setPage({ offset: this.dt.page.offset });
  }

  setDataTable() {
    this.dt = {
      page: {
        size: this.searchForm.get('PageSize')?.value,
        totalRecords: 0,
        totalPages: 0,
        offset: 0,
      },
      rows: [],
    };
  }

  setPage(pageInfo) {
    this.dt.page.offset = pageInfo.offset;
    this.startingIndex = this.dt.page.offset * this.dt.page.size

    this.searchForm.patchValue({
      Page: this.dt.page.offset + 1,
    });

    const params = this.searchForm.value;

    this.equipmentServices.getEquipmentRegistrationHistoricalData(this.data.valveType, this.data.wellStringId)
      .subscribe((pagedData) => {
        this.dt.page.totalPages = 1;
        this.dt.page.totalRecords = pagedData.data.length;
        this.dt.rows = pagedData.data;
      });
  }

  onSort(sort: any) {
    if (!sort) return;
    this.searchForm.patchValue({
      OrderBy: sort.active,
      OrderDir: sort.direction,
    });

    this.setPage({ offset: 0 });
  }

  handleFormatDate(dateString: string, withTime: boolean = false) {
    return handleFormatDate(dateString, withTime)
  }

  downloadEquipmentRegHistoricalRecords() {
    this.equipmentServices.downloadEquipmentRegHistoricalRecords(this.data.valveType, this.data.wellStringId).subscribe((res) => {
      let valveType = this.data.valveType == 'WRSV' ? 'Retrievable Valve / Plug' : this.data.valveType == 'TRSV' ? 'TRSCSSV' : this.data.valveType
      let fileName = valveType + ' Historical Records'
      saveAs(res, fileName);
    })
  }

  deleteHistoricalRecords(row) {
    const dialogRef = this.dialog.open(TwoLevelDeleteDialogComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const valveType = this.data.valveType == 'TR-SSSV' ? 'TR-SSSSV' : this.data.valveType;
        this.equipmentServices.deleteEquipmentHistory(valveType, this.data.wellStringId, row.id).subscribe((res) => {
          this.setPage({ offset: this.dt.page.offset });
          this.equipmentServices.getEquipmentRegistration(this.data.wellStringId).subscribe();
        })
      }
    });
  }

  updateHistoricalRecords(row, valveName) {
    const dialogRef = this.dialog.open(UpdateHistoricalDataComponent, { width: '70%', data: { row, valveName } });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.setPage({ offset: 0 });
        this.equipmentServices.getEquipmentRegistration(this.data.wellStringId).subscribe();
      }
    });
  }

}
